import React, { useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { Maximize2, Minimize2, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";

/**
 * Sabit header (900) ve menü (950) üstünde, body'ye portal ile render edilen modal kabuğu.
 * Dialog kullanmayan büyük modallar için UserModal ile aynı katman (1100).
 */
const PanelModal = ({
  open,
  onClose,
  children,
  className,
  align = "center",
  closeOnBackdrop = true,
}) => {
  if (!open || typeof document === "undefined") return null;

  const alignClass =
    align === "start"
      ? "items-start overflow-y-auto"
      : "items-center";

  return createPortal(
    <div
      className={cn(
        "panel-modal-overlay fixed inset-0 z-[1100] flex justify-center bg-black/60 backdrop-blur-sm p-2 sm:p-4",
        alignClass,
        className
      )}
      role="dialog"
      aria-modal="true"
      onClick={closeOnBackdrop ? onClose : undefined}
    >
      {children}
    </div>,
    document.body
  );
};

const PanelModalContent = ({ children, className, onClick }) => (
  <div className={cn("panel-modal-content", className)} onClick={onClick}>
    {children}
  </div>
);

/** Modal büyüt / küçült state — initialExpanded: açılışta büyük boyut */
const usePanelModalExpand = (initialExpanded = false) => {
  const [expanded, setExpanded] = useState(initialExpanded);
  const toggleExpanded = useCallback(() => setExpanded((v) => !v), []);
  const resetExpanded = useCallback(() => setExpanded(initialExpanded), [initialExpanded]);
  return { expanded, setExpanded, toggleExpanded, resetExpanded };
};

/** mode: fixed = sabit yükseklik, max = max-height tabanlı */
const panelModalSizeClasses = (expanded, mode = "fixed") => {
  if (mode === "max") {
    return expanded ? "panel-modal-content--expanded max-h-[95vh]" : "max-h-[90vh]";
  }
  return expanded ? "panel-modal-content--expanded h-[min(92vh,880px)]" : "h-[min(88vh,640px)]";
};

const PanelModalExpandButton = ({ expanded, onToggle, isDark, className }) => (
  <Button
    type="button"
    variant="ghost"
    size="sm"
    title={expanded ? "Küçült" : "Büyüt"}
    aria-label={expanded ? "Modalı küçült" : "Modalı büyüt"}
    onClick={onToggle}
    className={cn(
      "h-8 w-8 p-0 shrink-0",
      isDark ? "text-slate-400 hover:text-slate-200 hover:bg-slate-800" : "text-gray-500 hover:text-gray-800 hover:bg-gray-100",
      className
    )}
  >
    {expanded ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
  </Button>
);

const PanelModalHeaderActions = ({
  expanded,
  onToggle,
  onClose,
  isDark,
  showExpand = true,
  closeClassName,
}) => (
  <div className="flex items-center gap-1 shrink-0">
    {showExpand && (
      <PanelModalExpandButton expanded={expanded} onToggle={onToggle} isDark={isDark} />
    )}
    <Button
      type="button"
      variant="ghost"
      size="sm"
      onClick={onClose}
      title="Kapat"
      aria-label="Modalı kapat"
      className={cn(
        "h-8 w-8 p-0 shrink-0",
        isDark ? "text-slate-400 hover:text-red-400 hover:bg-slate-800" : "hover:bg-red-50 hover:text-red-600",
        closeClassName
      )}
    >
      <X className="w-4 h-4 sm:w-5 sm:h-5" />
    </Button>
  </div>
);

export {
  PanelModal,
  PanelModalContent,
  usePanelModalExpand,
  panelModalSizeClasses,
  PanelModalExpandButton,
  PanelModalHeaderActions,
};
